import { useRef, useState } from "react"
import { useDaily, useDailyEvent, useDevices, useInputSettings, useLocalParticipant } from "@daily-co/daily-react"
import { RefreshCw, X } from "lucide-react"
import { Button } from "components/ui/button"
import { BackgroundEffects } from "./BackgroundEffects"
import { CallSelect } from "./CallSelect"
import { describeUnsupportedNoiseReduction, hasCustomAudioTrack, isMicrophoneActive, readNativeNoiseReduction, supportsNativeNoiseReduction, updateNativeNoiseReduction } from "./nativeNoiseReduction"

type DeviceSelectProps = {
  id: string
  label: string
  devices: ReturnType<typeof useDevices>["cameras"]
  currentId?: string
  onChange: (deviceId: string) => void
  emptyLabel: string
  disabled?: boolean
}

/** Options for a CallSelect from Daily's device list; unlabeled devices get a numbered fallback. */
export const deviceOptions = (devices: ReturnType<typeof useDevices>["cameras"], label: string) =>
  devices.map((item, index) => {
    if (item.device.deviceId === "default") {
      const name = item.device.label.replace(/^default\s*(?:[-–—:]\s*)?/i, "").trim()
      return { value: "default", label: name ? `System default — ${name}` : "System default" }
    }
    return { value: item.device.deviceId, label: item.device.label || `${label} ${index + 1}` }
  })

function DeviceSelect({ id, label, devices, currentId, onChange, emptyLabel, disabled }: DeviceSelectProps) {
  return <CallSelect id={id} label={label} value={currentId || ""} options={deviceOptions(devices, label)} onChange={onChange} placeholder={emptyLabel} disabled={disabled} />
}

export const describeDeviceState = (state: ReturnType<typeof useDevices>["camState"], kind: "camera" | "microphone") => {
  switch (state) {
    case "blocked":
      return `${kind === "camera" ? "Camera" : "Microphone"} access is blocked. Allow it in your browser's site settings, then try again.`
    case "not-found":
      return `No ${kind} was found. You can still join${kind === "camera" ? " with audio only" : " and listen"}.`
    case "in-use":
      return `Your ${kind} is in use by another app. Close it, then try again.`
    case "not-supported":
    case "undefined-mediadevices":
      return `This browser cannot access your ${kind}. Try a current version of Chrome, Edge, Safari, or Firefox.`
    case "constraints-invalid":
    case "constraints-none-specified":
    case "unknown":
      return `We could not start your ${kind}. Try again or pick a different device.`
    default:
      return null
  }
}

/**
 * Camera, microphone, and speaker selection plus browser-permission recovery guidance.
 * Rendered inside the prejoin screen and as an in-call menu.
 */
export type DeviceSettingsSections = { camera?: boolean; microphone?: boolean; speaker?: boolean; noise?: boolean; refresh?: boolean }

const ALL_SECTIONS: Required<DeviceSettingsSections> = { camera: true, microphone: true, speaker: true, noise: true, refresh: true }

export function DeviceSettingsMenu({
  onClose,
  onMuteMicrophone,
  inline = false,
  showEffects = false,
  deviceLayout = "stacked",
  show = ALL_SECTIONS,
  testId = "call-audio-video-settings",
}: {
  onClose?: () => void
  onMuteMicrophone?: () => void
  inline?: boolean
  /** Prejoin shows Background effects inline; the in-call Settings dialog has its own pane. */
  showEffects?: boolean
  /** Prejoin can spread device selectors across the width beneath the preview. */
  deviceLayout?: "stacked" | "row"
  /** Which controls to render; the Settings dialog splits them across its Audio and Video panes. */
  show?: DeviceSettingsSections
  testId?: string
}) {
  const {
    cameras,
    microphones,
    speakers,
    currentCam,
    currentMic,
    currentSpeaker,
    camState,
    micState,
    setCamera,
    setMicrophone,
    setSpeaker,
    refreshDevices,
  } = useDevices()
  const { inputSettings, updateInputSettings } = useInputSettings()
  const daily = useDaily()
  const localParticipant = useLocalParticipant()
  const changingNoiseRef = useRef(false)
  const [changingNoiseReduction, setChangingNoiseReduction] = useState(false)
  const [noiseReductionError, setNoiseReductionError] = useState("")
  const [changingDevice, setChangingDevice] = useState(false)
  const [deviceError, setDeviceError] = useState("")
  const noiseReductionSupported = supportsNativeNoiseReduction() && !hasCustomAudioTrack(inputSettings)
  const microphoneActive = isMicrophoneActive(localParticipant?.audio, localParticipant?.tracks?.audio?.state)
  const changeDevice = async (label: string, change: () => Promise<void>) => {
    if (changingDevice) return
    setChangingDevice(true)
    setDeviceError("")
    try { await change() } catch { setDeviceError(`Could not ${label}. Check the device and try again.`) } finally { setChangingDevice(false) }
  }
  useDailyEvent("nonfatal-error", (event) => {
    if (event.type === "input-settings-error" && event.details?.category === "settings" && event.details?.media?.includes("audio")) {
      setNoiseReductionError("Noise reduction could not be changed. Check your microphone and try again.")
    }
  })
  const changeNoiseReduction = async (enabled: boolean) => {
    if (changingNoiseRef.current || !noiseReductionSupported || microphoneActive || !daily) return
    changingNoiseRef.current = true
    setChangingNoiseReduction(true)
    setNoiseReductionError("")
    try {
      await updateNativeNoiseReduction(daily, inputSettings, enabled, async (settings) => { await updateInputSettings(settings) })
    } catch {
      setNoiseReductionError("Noise reduction could not be changed. Check your microphone and try again.")
    } finally {
      changingNoiseRef.current = false
      setChangingNoiseReduction(false)
    }
  }
  const noiseReductionOn = readNativeNoiseReduction(inputSettings, localParticipant?.audio ? localParticipant.tracks?.audio?.persistentTrack : undefined)
  const cameraMessage = describeDeviceState(camState, "camera")
  const microphoneMessage = describeDeviceState(micState, "microphone")

  return (
    <div
      role={inline ? undefined : "dialog"}
      aria-label={inline ? undefined : "Audio and video settings"}
      data-testid={testId}
      className={inline ? "space-y-3" : "absolute bottom-24 left-1/2 z-[45] w-[min(22rem,calc(100vw-1.5rem))] -translate-x-1/2 space-y-3 rounded-2xl bg-[#0f1419] p-4 shadow-2xl ring-1 ring-white/10"}
    >
      {!inline && (
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-white">Audio and video</p>
          <button type="button" aria-label="Close settings" onClick={onClose} className="rounded-md p-1 text-white/70 hover:bg-white/10 hover:text-white">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      {(show.camera || show.microphone || show.speaker) && (
        <div className={deviceLayout === "row" ? "grid gap-3 sm:grid-cols-3" : "space-y-3"}>
          {show.camera && (
            <div className="min-w-0 space-y-1">
              <DeviceSelect id={`${testId}-camera`} label="Camera" devices={cameras} currentId={currentCam?.device.deviceId} onChange={(deviceId) => void changeDevice("change the camera", () => setCamera(deviceId))} emptyLabel="No camera found" disabled={changingDevice || changingNoiseReduction} />
              {cameraMessage && <p role="status" className="text-xs text-amber-200">{cameraMessage}</p>}
            </div>
          )}
          {show.microphone && (
            <div className="min-w-0 space-y-1">
              <DeviceSelect id={`${testId}-microphone`} label="Microphone" devices={microphones} currentId={currentMic?.device.deviceId} onChange={(deviceId) => void changeDevice("change the microphone", () => setMicrophone(deviceId))} emptyLabel="No microphone found" disabled={changingDevice || changingNoiseReduction} />
              {microphoneMessage && <p role="status" className="text-xs text-amber-200">{microphoneMessage}</p>}
            </div>
          )}
          {show.speaker && speakers.length > 0 && (
            <div className="min-w-0">
              <DeviceSelect id={`${testId}-speaker`} label="Speaker" devices={speakers} currentId={currentSpeaker?.device.deviceId} onChange={(deviceId) => void changeDevice("change the speaker", () => setSpeaker(deviceId))} emptyLabel="Default speaker" disabled={changingDevice || changingNoiseReduction} />
            </div>
          )}
        </div>
      )}
      {show.noise && (
      <label className="flex items-start gap-2 text-sm text-white">
        <input
          type="checkbox"
          checked={noiseReductionOn}
          onChange={(event) => void changeNoiseReduction(event.target.checked)}
          disabled={changingNoiseReduction || changingDevice || !noiseReductionSupported || microphoneActive || !daily}
          aria-describedby={noiseReductionError ? `${testId}-noise-error` : `${testId}-noise-help`}
          data-testid="call-noise-reduction"
          className="mt-1 h-4 w-4 accent-[#f6a35c]"
        />
        <span>
          <span className="block font-semibold">Noise reduction</span>
          <span id={`${testId}-noise-help`} className="block text-xs text-white/60">{!noiseReductionSupported ? describeUnsupportedNoiseReduction() : microphoneActive ? "Mute your microphone to change noise reduction." : "Reduces background noise using your browser's audio processing. On by default."}</span>
        </span>
      </label>
      )}
      {show.noise && noiseReductionSupported && localParticipant?.audio && (
        <Button type="button" variant="secondary" size="auto" disabled={changingDevice || changingNoiseReduction} onClick={() => void changeDevice("mute the microphone", async () => { if (onMuteMicrophone) onMuteMicrophone(); else daily?.setLocalAudio(false) })} className="h-9 px-3 text-xs">
          Mute microphone
        </Button>
      )}
      {show.noise && noiseReductionError && <p id={`${testId}-noise-error`} role="alert" className="text-xs text-amber-200">{noiseReductionError}</p>}
      {deviceError && <p role="alert" className="text-xs text-amber-200">{deviceError}</p>}
      {showEffects && <BackgroundEffects />}
      {show.refresh && (
        <Button type="button" variant="secondary" size="auto" disabled={changingDevice || changingNoiseReduction} onClick={() => void changeDevice("refresh devices", refreshDevices)} className="h-9 gap-2 px-3 text-xs">
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh devices
        </Button>
      )}
    </div>
  )
}
